/**
 * The account's writes, as outbox entries (`D194`) — the one place each
 * kind's `entityId`, stream and body are built, so no screen has to know the
 * table in `@repo/client/offline`'s header.
 *
 * Every builder is pure. {@link useOutboxWrite} is the one way a screen sends
 * one: it draws the effect (`outbox-optimistic.ts`) and queues the write, and
 * resolves once the write is on disk — not when the server answers, which
 * may be in a minute or after the next launch. A screen therefore closes its
 * sheet, says its toast and moves on at once, online or not.
 */

import { planKeys } from "@repo/client/data/plan";
import {
  EMPTY_OUTBOX,
  type OutboxEntry,
  type OutboxItem,
  type OutboxState,
  PLAN_STREAM,
} from "@repo/client/offline";
import type { ProposedItem } from "@repo/client/session";
import type {
  AddExclusionInput,
  CreateExerciseGoalInput,
  ExcludedExercise,
  ExclusionListOutput as ExclusionList,
  ExerciseGoalView,
  MeasurementInput,
  MeasurementListOutput,
  PlanItemRetargetInput,
  PlanItemView,
  PlanSessionUpdateInput,
  PutExercisePreferenceInput,
  SessionFeedbackInput,
  SorenessInput,
} from "@repo/contract/training";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useSyncExternalStore } from "react";
import { uuidv7 } from "../features/workout/store/uuid-v7";
import { useApi } from "./api";
import {
  applyOptimistic,
  type ExclusionPreview,
  type ExerciseGoalPreview,
  type GymUsePreview,
  type MeasurementUndoPreview,
  type PendingPlanItemView,
  type PlanItemPreview,
  type PlanSessionPreview,
  touchedBy,
} from "./outbox-optimistic";
import { OutboxContext, type OutboxRunner } from "./outbox-runner";
import {
  createOutboxSend,
  exclusionAnswers,
  outboxInvalidations,
} from "./outbox-send";

/** A fresh client id, from the helper that works on an http origin. */
export function newClientId(): string {
  return uuidv7(Date.now());
}

// ── The plan ─────────────────────────────────────────────────────────────────

/**
 * The line a card becomes while its add is on the way: the exercise as the
 * card shows it, and NO target — the dose is the engine's (`D182`), so the
 * row says it is being prescribed (`pendingDose`, `T-156`).
 */
export function placeholderLine(
  card: Pick<
    ProposedItem,
    | "clip"
    | "coachingTip"
    | "difficultyLevel"
    | "equipment"
    | "exerciseId"
    | "name"
    | "primaryMuscle"
    | "secondaryMuscles"
    | "tracks"
  >,
  itemId: string,
  sessionId: string
): PendingPlanItemView {
  return {
    exercise: {
      clip: card.clip,
      ...(card.coachingTip === undefined
        ? {}
        : { coachingTip: card.coachingTip }),
      ...(card.difficultyLevel === undefined
        ? {}
        : { difficultyLevel: card.difficultyLevel as never }),
      equipment: { id: card.equipment.id ?? 0, name: card.equipment.name },
      name: card.name,
      primaryMuscle: card.primaryMuscle,
      ...(card.secondaryMuscles === undefined
        ? {}
        : { secondaryMuscles: [...card.secondaryMuscles] }),
      ...(card.tracks === undefined ? {} : { tracks: card.tracks }),
    },
    exerciseId: card.exerciseId,
    itemId,
    notes: null,
    pendingDose: true,
    position: 0,
    reason: null,
    restSeconds: null,
    sessionId,
    targetDurationSeconds: null,
    targetLoad: null,
    targetRepsMax: null,
    targetRepsMin: null,
    targetSets: null,
  };
}

/**
 * The line a replace puts in the slot, drawn exactly: the replace keeps the
 * old line's sets, reps, hold, rest and notes and drops its load and reason
 * (`replace-plan-item.ts`), so nothing here is a guess.
 */
export function replacementLine(
  old: PlanItemView,
  card: Parameters<typeof placeholderLine>[0],
  itemId: string
): PendingPlanItemView {
  const shown = placeholderLine(card, itemId, old.sessionId);
  const { pendingDose: _never, ...exact } = shown;
  return {
    ...exact,
    notes: old.notes,
    position: old.position,
    restSeconds: old.restSeconds,
    targetDurationSeconds: old.targetDurationSeconds ?? null,
    targetLoad: null,
    targetRepsMax: old.targetRepsMax,
    targetRepsMin: old.targetRepsMin,
    targetSets: old.targetSets,
  };
}

export function planItemAdd(input: {
  readonly card: Parameters<typeof placeholderLine>[0];
  readonly planId: string;
  /** The engine's eligibility before the add (`D227`): the workout's
   *  "No plano" for an exercise added today (`D254`). */
  readonly requireEligible?: boolean;
  readonly sessionId: string;
}): OutboxEntry {
  const id = newClientId();
  const preview: PlanItemPreview = {
    item: placeholderLine(input.card, id, input.sessionId),
  };
  return {
    // `prescribe` and no target: the server writes the dose (`D182`).
    body: {
      exerciseId: input.card.exerciseId,
      id,
      planId: input.planId,
      prescribe: true,
      ...(input.requireEligible === true ? { requireEligible: true } : {}),
      sessionId: input.sessionId,
    },
    dependsOn: PLAN_STREAM,
    entityId: id,
    kind: "plan.item.add",
    preview,
  };
}

export function planItemReplace(input: {
  readonly card: Parameters<typeof placeholderLine>[0];
  readonly old: PlanItemView;
  readonly planId: string;
}): OutboxEntry {
  const id = newClientId();
  const preview: PlanItemPreview = {
    item: replacementLine(input.old, input.card, id),
  };
  return {
    body: { exerciseId: input.card.exerciseId, id, planId: input.planId },
    dependsOn: PLAN_STREAM,
    entityId: input.old.itemId,
    kind: "plan.item.replace",
    preview,
  };
}

export function planItemRemove(itemId: string): OutboxEntry {
  return {
    body: undefined,
    dependsOn: PLAN_STREAM,
    entityId: itemId,
    kind: "plan.item.remove",
  };
}

/** The undo of a remove, or with `replacing` of a replace (`D182`). */
export function planItemRestore(input: {
  readonly item: PlanItemView;
  readonly planId: string;
  readonly replacing?: string;
}): OutboxEntry {
  const preview: PlanItemPreview = {
    item: input.item,
    ...(input.replacing === undefined ? {} : { replacing: input.replacing }),
  };
  return {
    body: {
      planId: input.planId,
      ...(input.replacing === undefined ? {} : { replacing: input.replacing }),
    },
    dependsOn: PLAN_STREAM,
    entityId: input.item.itemId,
    kind: "plan.item.restore",
    preview,
  };
}

export function planSessionUpdate(
  sessionId: string,
  input: PlanSessionUpdateInput
): OutboxEntry {
  return {
    body: input,
    dependsOn: PLAN_STREAM,
    entityId: sessionId,
    kind: "plan.session.update",
  };
}

/** The numbers a person may set on a line by hand (`D197`). */
export type LineTargets = Pick<
  PlanItemRetargetInput,
  | "restSeconds"
  | "targetDurationSeconds"
  | "targetRepsMax"
  | "targetRepsMin"
  | "targetSets"
>;

/** What an ended workout carries into a line when the person accepts it
 *  (`D247`): the sets they did by hand, the load they kept choosing. */
export type CarriedTargets = Pick<
  PlanItemRetargetInput,
  "targetLoad" | "targetSets"
>;

/**
 * The person's own sets, reps, hold or rest for one line (`D197`). A line is
 * immutable, so the change is a NEW line in the same slot — drawn exactly,
 * since every number on it is the person's — and its undo is a restore of
 * the old one with `replacing` the new (`planItemRestore`).
 */
export function planItemRetarget(input: {
  readonly old: PlanItemView;
  readonly planId: string;
  /** The person's own numbers (`D197`), or today's numbers carried into
   *  the plan (`D247`), whose load the retarget route already accepts. */
  readonly targets: LineTargets | CarriedTargets;
}): OutboxEntry {
  const id = newClientId();
  const preview: PlanItemPreview = {
    item: { ...input.old, ...input.targets, itemId: id },
  };
  return {
    body: { id, planId: input.planId, ...input.targets },
    dependsOn: PLAN_STREAM,
    entityId: input.old.itemId,
    kind: "plan.item.retarget",
    preview,
  };
}

/**
 * One line to another day of the plan (`D214`), whole: the NEW line's client
 * id makes the move replayable, and the preview is the same line under that
 * id, drawn at the end of the target day.
 */
export function planItemMove(input: {
  readonly line: PlanItemView;
  readonly planId: string;
  readonly sessionId: string;
}): OutboxEntry {
  const id = newClientId();
  const preview: PlanItemPreview = {
    item: { ...input.line, itemId: id, sessionId: input.sessionId },
  };
  return {
    body: { id, planId: input.planId, sessionId: input.sessionId },
    dependsOn: PLAN_STREAM,
    entityId: input.line.itemId,
    kind: "plan.item.move",
    preview,
  };
}

/**
 * A new, empty day at the end of the plan (`D197`), under a client id so the
 * add is replayable and the day is drawn — and can be filled — at once.
 */
export function planSessionAdd(input: {
  readonly label: string;
  readonly planId: string;
}): OutboxEntry {
  const id = newClientId();
  const preview: PlanSessionPreview = {
    session: {
      items: [],
      label: input.label,
      // Past the last: the patch renumbers it against what the plan holds.
      position: Number.MAX_SAFE_INTEGER,
      sessionId: id,
      warmup: { firstCompoundSets: [], minutes: 0 },
      weekday: null,
    },
  };
  return {
    body: { id, label: input.label, planId: input.planId },
    dependsOn: PLAN_STREAM,
    entityId: id,
    kind: "plan.session.add",
    preview,
  };
}

/**
 * The plan's days in a new order (`D210`): every visible day, once each. One
 * entity per plan, so a second move queued behind the first replaces nothing
 * — both ride the plan stream in order, and the last one wins, as on screen.
 */
export function planSessionsReorder(
  planId: string,
  sessionIds: readonly string[]
): OutboxEntry {
  return {
    body: { planId, sessionIds: [...sessionIds] },
    dependsOn: PLAN_STREAM,
    entityId: planId,
    kind: "plan.sessions.reorder",
  };
}

/**
 * Lines of one day grouped as a superset, or taken out of theirs (`D246`).
 * Keyed on the first line it names, on the plan stream: it names lines an
 * add before it created, and a reorder behind it must find them grouped. A
 * replay that finds the lines as asked writes nothing.
 */
export function planSuperset(
  planId: string,
  change: { readonly grouped: boolean; readonly itemIds: readonly string[] }
): OutboxEntry {
  return {
    body: { grouped: change.grouped, itemIds: [...change.itemIds], planId },
    dependsOn: PLAN_STREAM,
    entityId: change.itemIds[0] ?? planId,
    kind: "plan.superset",
  };
}

/** A day and its lines, off the plan (`D197`). No undo: the screen asks. */
export function planSessionRemove(sessionId: string): OutboxEntry {
  return {
    body: undefined,
    dependsOn: PLAN_STREAM,
    entityId: sessionId,
    kind: "plan.session.remove",
  };
}

export function suggestionDismiss(): OutboxEntry {
  const kind = "plan.suggestion.dismiss";
  return { body: undefined, dependsOn: kind, entityId: kind, kind };
}

/**
 * "Onde você treina hoje?" (`D202`): the gym's list becomes the equipment in
 * effect. One stream for every switch, so the last tap is the one that stays;
 * the preview draws the chip and the checklist at the tap.
 */
export function gymUse(gym: {
  readonly equipmentIds: readonly number[];
  readonly id: string;
  readonly place: GymUsePreview["place"];
}): OutboxEntry {
  const kind = "gym.use";
  const preview: GymUsePreview = {
    equipmentIds: [...gym.equipmentIds],
    place: gym.place,
  };
  return {
    body: { gymId: gym.id },
    dependsOn: kind,
    entityId: kind,
    kind,
    preview,
  };
}

// ── Exclusions ───────────────────────────────────────────────────────────────

export function exclusionAdd(
  input: AddExclusionInput,
  row: Omit<ExcludedExercise, "excludedAt">
): OutboxEntry {
  const preview: ExclusionPreview = {
    row: { ...row, excludedAt: new Date().toISOString() },
  };
  return {
    body: input,
    dependsOn: input.exerciseId,
    entityId: input.exerciseId,
    kind: "exclusion.add",
    preview,
  };
}

export function exclusionRemove(exerciseId: string): OutboxEntry {
  return {
    body: undefined,
    dependsOn: exerciseId,
    entityId: exerciseId,
    kind: "exclusion.remove",
  };
}

// ── The person's own exercise data (`D200`) ─────────────────────────────────

/** The pinned note, replaced whole. Its stream is the exercise, so a clear
 *  queued after a write goes after it. */
export function exerciseNotePut(exerciseId: string, text: string): OutboxEntry {
  return {
    body: { body: text },
    dependsOn: exerciseId,
    entityId: exerciseId,
    kind: "exercise-note.put",
  };
}

export function exerciseNoteRemove(exerciseId: string): OutboxEntry {
  return {
    body: undefined,
    dependsOn: exerciseId,
    entityId: exerciseId,
    kind: "exercise-note.remove",
  };
}

/** The star or a chip: only the key the tap changed is sent. */
export function exercisePreferencePut(
  exerciseId: string,
  input: PutExercisePreferenceInput
): OutboxEntry {
  return {
    body: input,
    dependsOn: exerciseId,
    entityId: exerciseId,
    kind: "exercise-preference.put",
  };
}

/** A goal under a client id, drawn at once with the best the sheet showed. */
export function exerciseGoalAdd(
  input: Omit<CreateExerciseGoalInput, "id">,
  shown: Pick<ExerciseGoalView, "current" | "name">
): OutboxEntry {
  const id = newClientId();
  const preview: ExerciseGoalPreview = {
    goal: {
      achievedAt: null,
      createdAt: new Date().toISOString(),
      current: shown.current,
      deadline: input.deadline ?? null,
      exerciseId: input.exerciseId,
      goalId: id,
      metric: input.metric,
      ...(shown.name === undefined ? {} : { name: shown.name }),
      targetKg: input.targetKg,
    },
  };
  return {
    body: { ...input, id },
    dependsOn: id,
    entityId: id,
    kind: "exercise-goal.add",
    preview,
  };
}

export function exerciseGoalRemove(goalId: string): OutboxEntry {
  return {
    body: undefined,
    dependsOn: goalId,
    entityId: goalId,
    kind: "exercise-goal.remove",
  };
}

// ── The profile and the account ──────────────────────────────────────────────

/** The `PUT`s whose stream is the kind itself (`outbox.ts`). */
export type SectionKind =
  | "equipment.put"
  | "injuries.put"
  | "me.display-name.put"
  | "me.locale.put"
  | "me.time-zone.put"
  | "me.weight-unit.put"
  | "notification-prefs.put"
  | "parq.put"
  | "profile.put"
  | "training-config.put";

export function sectionPut(kind: SectionKind, body: unknown): OutboxEntry {
  return { body, dependsOn: kind, entityId: kind, kind };
}

/** A weigh-in. The idempotency key is the entity: a replay is the same row. */
export function measurementAdd(
  input: MeasurementInput & { readonly idempotencyKey: string }
): OutboxEntry {
  return {
    body: input,
    dependsOn: input.idempotencyKey,
    entityId: input.idempotencyKey,
    kind: "measurement.add",
  };
}

/**
 * The undo of a weigh-in: rides the add's stream, so it goes after the add
 * has answered with the row's id (`outbox-send.ts`, `measurementIds`).
 */
export function measurementDelete(input: {
  readonly idempotencyKey: string;
  readonly measurementId?: string;
  readonly previousLatest: MeasurementListOutput["latest"];
}): OutboxEntry {
  const preview: MeasurementUndoPreview = {
    previousLatest: input.previousLatest,
  };
  return {
    body:
      input.measurementId === undefined
        ? undefined
        : { measurementId: input.measurementId },
    dependsOn: input.idempotencyKey,
    entityId: input.idempotencyKey,
    kind: "measurement.delete",
    preview,
  };
}

// ── The session's follow-ups ─────────────────────────────────────────────────

/**
 * "End the other and start new" (`D177`): another device's open session,
 * ended `abandoned`, through the outbox (`D194`). Queued before the new
 * session's start, which the outbox holds behind it until it has gone, so
 * the start never meets the open session (`outbox.ts`, `isBlocked`).
 */
export function abandonEnd(sessionId: string, body: object): OutboxEntry {
  return {
    body,
    dependsOn: sessionId,
    entityId: sessionId,
    kind: "session.end",
  };
}

/** Rides the session's stream: never ahead of its start or its end. */
export function sorenessPut(
  sessionId: string,
  body: SorenessInput
): OutboxEntry {
  return {
    body,
    dependsOn: sessionId,
    entityId: sessionId,
    kind: "session.soreness",
  };
}

export function feedbackPut(
  sessionId: string,
  body: SessionFeedbackInput
): OutboxEntry {
  return {
    body,
    dependsOn: sessionId,
    entityId: sessionId,
    kind: "session.feedback",
  };
}

// ── Sending one ──────────────────────────────────────────────────────────────

/**
 * Queue an account write and draw its effect (header). The effect is drawn
 * BEFORE the disk write, from a provisional item, so the tap and the screen
 * move in the same frame; should the queue refuse the entry (a programming
 * error, `OutboxEntryError`), what the effect touched is invalidated and the
 * error is rethrown to the caller.
 *
 * Without a `DataProvider` — a story, a component test — there is no queue:
 * the write is SENT at once through the same sender the queue uses, and its
 * failure reaches the caller. The app always has the queue.
 */
export function useOutboxWrite(): (entry: OutboxEntry) => Promise<OutboxItem> {
  const runner = useContext(OutboxContext);
  const client = useQueryClient();
  const api = useApi();
  return useCallback(
    async (entry: OutboxEntry) => {
      const provisional: OutboxItem = {
        accountId: null,
        attempts: 0,
        body: entry.body,
        createdAt: new Date().toISOString(),
        dependsOn: entry.dependsOn,
        entityId: entry.entityId,
        id: "provisional",
        kind: entry.kind,
        lastError: null,
        nextAttemptAt: null,
        ...(entry.preview === undefined ? {} : { preview: entry.preview }),
        state: "pending",
      };
      applyOptimistic(client, provisional);
      if (runner === null) {
        const answer = await createOutboxSend(api)(provisional);
        if (entry.kind === "exclusion.add") {
          exclusionAnswers.set(
            entry.entityId,
            (answer as { created: boolean }).created
          );
        }
        for (const queryKey of outboxInvalidations(api)(entry.kind)) {
          client.invalidateQueries({ queryKey }).catch(() => undefined);
        }
        return provisional;
      }
      try {
        return await runner.enqueue(entry);
      } catch (error) {
        for (const queryKey of touchedBy(entry.kind)) {
          client.invalidateQueries({ queryKey }).catch(() => undefined);
        }
        throw error;
      }
    },
    [api, client, runner]
  );
}

/**
 * Whether every plan write the outbox holds has landed — what a PUBLISH
 * waits for (`D194`). Publishing archives the plan in effect, and a remove
 * or a swap still queued against it would then name a plan that is no
 * longer the account's and be refused. One drain is asked for and awaited;
 * `false` means some are still on their way (offline, backing off). A
 * refused one does not hold the publish: it stays in the quarantine.
 */
export async function planWritesSettled(
  runner: OutboxRunner | null
): Promise<boolean> {
  if (runner === null) {
    return true;
  }
  const waiting = () =>
    runner.store
      .getState()
      .items.some(
        (item) => item.dependsOn === PLAN_STREAM && item.state !== "quarantined"
      );
  if (!waiting()) {
    return true;
  }
  runner.trigger();
  await runner.idle();
  return !waiting();
}

/**
 * The answer `useExclusionWrites().add` resolves with: `created` is read when
 * it is asked for. Outside the hook on purpose — a getter in an object
 * literal is one of the shapes React Compiler cannot lower, and it would skip
 * the whole hook (`T-168`).
 */
function exclusionAnswer(
  exerciseId: string,
  guess: boolean
): { readonly created: boolean } {
  return {
    get created() {
      return exclusionAnswers.get(exerciseId) ?? guess;
    },
  };
}

/** Whether any cached refusal list already names the exercise. */
function alreadyExcluded(client: QueryClient, exerciseId: string): boolean {
  return client
    .getQueriesData<ExclusionList>({ queryKey: planKeys.exclusionsAll })
    .some(([, list]) =>
      (list?.rows ?? []).some((row) => row.exerciseId === exerciseId)
    );
}

/**
 * The refusal list's two writes, through the outbox. `add` answers whether
 * THIS write made the exclusion — what an undo reads before taking it back
 * (`swap/sheet.tsx`). `created` is read when the undo needs it: the server's
 * 201-or-200 once the write has landed (`exclusionAnswers`), and until then
 * what the device's cached refusal list said when the person tapped.
 */
export function useExclusionWrites() {
  const write = useOutboxWrite();
  const client = useQueryClient();
  const add = useCallback(
    async (
      exerciseId: string,
      row: Omit<ExcludedExercise, "excludedAt" | "exerciseId">
    ): Promise<{ readonly created: boolean }> => {
      const guess = !alreadyExcluded(client, exerciseId);
      exclusionAnswers.delete(exerciseId);
      await write(exclusionAdd({ exerciseId }, { ...row, exerciseId }));
      return exclusionAnswer(exerciseId, guess);
    },
    [client, write]
  );
  const remove = useCallback(
    (exerciseId: string) => write(exclusionRemove(exerciseId)),
    [write]
  );
  return { add, remove };
}

const noSubscription = () => () => undefined;
const emptyState = (): OutboxState => EMPTY_OUTBOX;

/**
 * Every queued item, or none where no `DataProvider` is mounted — a story or
 * a component test renders the screens without an outbox runner, and reading
 * what is queued must not be what breaks them.
 */
export function useQueuedItems(): readonly OutboxItem[] {
  const runner = useContext(OutboxContext);
  const state = useSyncExternalStore(
    runner?.store.subscribe ?? noSubscription,
    runner?.store.getState ?? emptyState,
    runner?.store.getState ?? emptyState
  );
  return state.items;
}

/** The live items of one kind and entity — what a screen shows as "sent,
 *  still on its way" (a soreness answer, a feedback). */
export function liveItems(
  items: readonly OutboxItem[],
  kind: OutboxItem["kind"],
  entityId: string
): readonly OutboxItem[] {
  return items.filter(
    (item) =>
      item.kind === kind &&
      item.entityId === entityId &&
      item.state !== "quarantined"
  );
}
